import { _ } from '@sveltia/i18n';

import { fetchAPI } from '$lib/services/backends/git/shared/api';
import { apiConfig } from '$lib/services/backends/git/shared/api';
import { repository } from '$lib/services/backends/git/github/repository';
import { user } from '$lib/services/user/account.svelte';

import { isGitbaseAdminSite } from './mode';

const AGENT_PROXY_BASE = '/admin/cms/agent';

const AGENT_API_HEADERS = {
  Accept: 'application/vnd.github+json',
  'X-GitHub-Api-Version': '2026-03-10',
};

/** @typedef {'queued' | 'in_progress' | 'completed' | 'failed' | 'idle' | 'waiting_for_user' | 'timed_out' | 'cancelled'} AgentTaskState */

/**
 * @typedef {object} AgentTask
 * @property {string} [id]
 * @property {string} [html_url]
 * @property {AgentTaskState} [state]
 * @property {{ type?: string, data?: { head_ref?: string } }[]} [artifacts]
 * @property {{ head_ref?: string }[]} [sessions]
 */

/**
 * @param {Response} response
 * @returns {Promise<never>}
 */
const throwAgentResponseError = async (response) => {
  let message = `Request failed (${response.status})`;

  try {
    const body = await response.json();

    if (typeof body?.message === 'string') {
      message = body.message;
    }
  } catch {
    //
  }

  throw new Error('Server responded with an error', { cause: { status: response.status, message } });
};

/**
 * @param {string} path
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {Record<string, unknown>} [options.body]
 * @returns {Promise<unknown>}
 */
const agentProxyFetch = async (path, { method = 'GET', body } = {}) => {
  const token = user.account?.token;

  if (!token) {
    throw new Error('You are not signed in.');
  }

  const response = await fetch(`${AGENT_PROXY_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    await throwAgentResponseError(response);
  }

  if (response.status === 204) {
    return undefined;
  }

  return response.json();
};

/**
 * @param {Error} error
 * @returns {string}
 */
export const formatAgentError = (error) => {
  const cause = /** @type {{ status?: number, message?: string } | undefined} */ (error.cause);
  const status = cause?.status;
  const detail = cause?.message?.trim();

  if (status === 403 || status === 402) {
    return detail
      ? _('agent_error_access_denied_detail', { values: { detail } })
      : _('agent_error_access_denied');
  }

  if (status === 401) {
    return _('agent_error_token_expired');
  }

  if (status === 409) {
    if (detail && /cca/i.test(detail)) {
      return _('agent_error_cca_disabled');
    }

    return detail || _('agent_error_conflict');
  }

  return detail || error.message || _('agent_error_generic');
};

/**
 * @param {AgentTaskState} state
 * @returns {string}
 */
export const getAgentStateLabel = (state) => {
  switch (state) {
    case 'queued':
      return _('agent_state_queued');
    case 'in_progress':
    case 'idle':
      return _('agent_state_in_progress');
    case 'waiting_for_user':
      return _('agent_state_waiting_for_user');
    case 'completed':
      return _('agent_state_completed');
    case 'failed':
      return _('agent_state_failed');
    case 'timed_out':
      return _('agent_state_timed_out');
    case 'cancelled':
      return _('agent_state_cancelled');
    default:
      return _('agent_state_updating');
  }
};

/** @param {AgentTaskState} state @returns {boolean} */
export const isAgentTaskActive = (state) =>
  state === 'queued' ||
  state === 'in_progress' ||
  state === 'idle' ||
  state === 'waiting_for_user';

/**
 * @param {object} args
 * @param {string} args.prompt
 * @param {string} [args.baseRef]
 * @returns {Promise<{ id: string, html_url?: string, state: AgentTaskState }>}
 */
export const createAgentTask = async ({ prompt, baseRef }) => {
  const { owner, repo, branch } = repository;
  const resolvedBaseRef = baseRef ?? branch ?? 'main';

  if (isGitbaseAdminSite()) {
    return /** @type {Promise<{ id: string, html_url?: string, state: AgentTaskState }>} */ (
      agentProxyFetch('/tasks', {
        method: 'POST',
        body: {
          prompt,
          base_ref: resolvedBaseRef,
          create_pull_request: false,
        },
      })
    );
  }

  if (!owner || !repo) {
    throw new Error('Repository is not configured.');
  }

  return /** @type {Promise<{ id: string, html_url?: string, state: AgentTaskState }>} */ (
    fetchAPI(`/agents/repos/${owner}/${repo}/tasks`, {
      method: 'POST',
      headers: AGENT_API_HEADERS,
      body: {
        prompt,
        base_ref: resolvedBaseRef,
        create_pull_request: false,
      },
    })
  );
};

/**
 * @param {string} taskId
 * @returns {Promise<AgentTask & { id: string, state: AgentTaskState }>}
 */
export const getAgentTask = async (taskId) => {
  if (isGitbaseAdminSite()) {
    return /** @type {Promise<AgentTask & { id: string, state: AgentTaskState }>} */ (
      agentProxyFetch(`/tasks/${encodeURIComponent(taskId)}`)
    );
  }

  return /** @type {Promise<AgentTask & { id: string, state: AgentTaskState }>} */ (
    fetchAPI(`/agents/tasks/${encodeURIComponent(taskId)}`, {
      headers: AGENT_API_HEADERS,
    })
  );
};

/**
 * @param {string} taskId
 * @param {string} content
 * @returns {Promise<void>}
 */
export const steerAgentTask = async (taskId, content) => {
  if (isGitbaseAdminSite()) {
    await agentProxyFetch(`/tasks/${encodeURIComponent(taskId)}/steer`, {
      method: 'POST',
      body: { content, type: 'user_message' },
    });

    return;
  }

  const { restBaseURL, authScheme = 'token' } = apiConfig;
  const token = user.account?.token;

  if (!token) {
    throw new Error('You are not signed in.');
  }

  const response = await fetch(`${restBaseURL}/agents/tasks/${encodeURIComponent(taskId)}/steer`, {
    method: 'POST',
    headers: {
      ...AGENT_API_HEADERS,
      Authorization: `${authScheme} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content, type: 'user_message' }),
  });

  if (!response.ok) {
    await throwAgentResponseError(response);
  }
};

/**
 * @param {AgentTask} [task]
 * @returns {string | undefined}
 */
export const getAgentBranchName = (task) => {
  const artifacts = task?.artifacts ?? [];

  for (const artifact of artifacts) {
    if (artifact?.type === 'branch' && artifact?.data?.head_ref) {
      return artifact.data.head_ref;
    }
  }

  const sessions = task?.sessions ?? [];

  return sessions.at(-1)?.head_ref;
};
